import { defineStore } from 'pinia'
import rentrollApi from '@/api/rentroll'
import { useUserStore } from '@/stores/user'
import type { CreateRentRollAttributes, RentRollAttributes } from 'common'
import { notStarted, loading, loaded, error, type Loading, isLoaded } from '@/utils/loading'

export const useRentRollStore = defineStore('rentroll', {
  state: () => ({
    savedRentRolls: notStarted as Loading<RentRollAttributes[]>,
    currentRentRoll: notStarted as Loading<RentRollAttributes>
  }),
  actions: {
    async saveRentRoll(rentRollData: CreateRentRollAttributes): Promise<any> {
      try {
        const saved = await rentrollApi.saveRentRoll(rentRollData)
        if (isLoaded(this.savedRentRolls)) {
          this.savedRentRolls.data.unshift(saved)
        }
        return saved
      } catch (err) {
        console.error('Failed to save rent roll:', err)
        return null
      }
    },

    async downloadRentRoll(rentRollId: number, filename: string): Promise<void> {
      const file = await rentrollApi.getRentRollFile(rentRollId)

      // Convert base64 to byte array
      const binaryString = atob(file)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }

      // Determine MIME type based on file extension
      const extension = filename.split('.').pop()?.toLowerCase()
      let mimeType = 'application/octet-stream'
      if (extension === 'pdf') {
        mimeType = 'application/pdf'
      } else if (extension === 'xlsx' || extension === 'xls') {
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      } else if (extension === 'csv') {
        mimeType = 'text/csv'
      }

      const blob = new Blob([bytes], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      // Clean up the object URL to prevent memory leaks
      URL.revokeObjectURL(url)
    },

    async refreshSavedRentRolls(): Promise<void> {
      this.savedRentRolls = loading
      try {
        const rentRolls = await rentrollApi.listRentRolls()
        this.savedRentRolls = loaded(rentRolls)
      } catch (err) {
        console.error('Failed to fetch saved rent rolls:', err)
        this.savedRentRolls = error(err as Error)
      }
    },

    async getRentRoll(
      id: number,
      options?: { logVisit?: boolean }
    ): Promise<Loading<RentRollAttributes>> {
      this.currentRentRoll = loading
      try {
        const rentRoll = await rentrollApi.getRentRoll(id, options)
        if (options?.logVisit) {
          useUserStore().recordReportVisitOffline({
            report_type: 'rent_roll',
            report_id: rentRoll.id,
            report_name: rentRoll.name
          })
        }
        this.currentRentRoll = loaded(rentRoll)
        return this.currentRentRoll
      } catch (err) {
        console.error('Failed to fetch rent roll:', err)
        this.currentRentRoll = error(err as Error)
        return this.currentRentRoll
      }
    },

    async updateRentRoll(
      id: number,
      data: {
        name?: string
        lease_data?: any[]
        as_of_date?: Date | null
        metadata?: Record<string, unknown> | null
        reviewed?: boolean
      }
    ): Promise<boolean> {
      try {
        const updated = await rentrollApi.updateRentRoll(id, data)
        // Update in the saved list
        if (isLoaded(this.savedRentRolls)) {
          const index = this.savedRentRolls.data.findIndex((rr: RentRollAttributes) => rr.id === id)
          if (index !== -1) {
            this.savedRentRolls.data[index] = { ...this.savedRentRolls.data[index], ...updated }
          }
        }
        // Update current rent roll if it's the same one
        if (isLoaded(this.currentRentRoll) && this.currentRentRoll.data.id === id) {
          this.currentRentRoll.data = { ...this.currentRentRoll.data, ...updated }
        }
        return true
      } catch (err) {
        console.error('Failed to update rent roll:', err)
        return false
      }
    },

    async deleteRentRoll(id: number): Promise<boolean> {
      try {
        await rentrollApi.deleteRentRoll(id)
        // Remove from the saved list
        if (isLoaded(this.savedRentRolls)) {
          this.savedRentRolls.data = this.savedRentRolls.data.filter(
            (rr: RentRollAttributes) => rr.id !== id
          )
        }
        useUserStore().removeReportVisit('rent_roll', id)
        return true
      } catch (err) {
        console.error('Failed to delete rent roll:', err)
        return false
      }
    },

    clearCurrentRentRoll() {
      this.currentRentRoll = notStarted
    }
  },
  getters: {
    getSavedRentRolls(state): Loading<RentRollAttributes[]> {
      return state.savedRentRolls
    },
    getCurrentRentRoll(state): Loading<RentRollAttributes> {
      return state.currentRentRoll
    }
  }
})
